import React from 'react';
import { Box, Button, Badge, useDisclosure, Collapse } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faCircle,
  faCircleCheck,
  faXmarkCircle,
  faChevronUp,
  faChevronDown,
  faCircleChevronRight,
  faExclamationCircle,
  faClock
} from '@fortawesome/pro-regular-svg-icons';

export enum Status {
  Todo = 'Todo',
  InProgress = 'InProgress',
  Complete = 'Complete',
  InError = 'InError',
  Rejected = 'Rejected',
  Waiting = 'Waiting',
  Locked = 'Locked'
}

export interface Props {
  status: Status;
  label: string;
  'data-selector'?: string;
  id?: string;
  onClick?: () => void;
  completeLabel?: string;
  resolveLabel?: string;
  buttonLabel?: string;
  hideButton?: boolean;
  noEdit?: boolean;
  hasStart?: boolean;
  reason?: string;
  showAction?: boolean;
  actions?: React.ReactNode;
}

const Finger: React.FC<Props> = (props: Props) => {
  const {
    id = 'finger',
    status,
    label,
    onClick,
    completeLabel,
    resolveLabel,
    buttonLabel,
    hideButton,
    noEdit,
    hasStart,
    showAction,
    reason,
    'data-selector': selector = 'finger'
  } = props;

  const hasEditOrResume =
    (status !== Status.Todo && noEdit !== true) ||
    (noEdit === true && status !== Status.Complete && status !== Status.Todo);

  const isNotLocked = status !== Status.Locked;

  const isSectionStarted = hasStart && !hasEditOrResume;

  const shouldShowAction = status !== Status.Locked && showAction;

  const isClickable =
    (![Status.Todo, Status.Locked].includes(status) && hideButton !== true) ||
    (isNotLocked && shouldShowAction && (hasEditOrResume || isSectionStarted));

  const { isOpen, onToggle } = useDisclosure();

  const isInteractive = isClickable || reason;

  const getOnClick = () => {
    if (isClickable) {
      return onClick;
    } else if (reason) {
      return onToggle;
    } else {
      return undefined;
    }
  };

  const getProps = (status: Status) => {
    switch (status) {
      case Status.Todo:
        return {
          Icon: {
            icon: faCircle,
            iconColor: 'muted'
          },
          Badge: null
        };
      case Status.InProgress:
        return {
          Icon: {
            icon: faCircleChevronRight,
            iconColor: 'orange.400'
          },
          Badge: {
            colorScheme: 'orange',
            children: buttonLabel || 'To do'
          }
        };
      case Status.Complete:
        return {
          Icon: {
            icon: faCircleCheck,
            iconColor: 'green.400'
          },
          Badge: {
            colorScheme: 'green',
            children: completeLabel || 'Done'
          }
        };
      case Status.InError:
        return {
          Icon: {
            icon: faExclamationCircle,
            iconColor: 'red.400'
          },
          Badge: {
            colorScheme: 'red',
            children: resolveLabel || 'View/Resolve'
          }
        };
      case Status.Rejected:
        return {
          Icon: {
            icon: faXmarkCircle,
            iconColor: 'red.400'
          },
          Badge: {
            colorScheme: 'red',
            children: 'Rejected'
          }
        };
      case Status.Waiting:
        return {
          Icon: {
            icon: faClock,
            iconColor: 'muted'
          }
        };
      case Status.Locked:
        return {
          Icon: {
            icon: faCircleCheck,
            iconColor: 'green.400'
          },
          Badge: {
            colorScheme: 'green',
            children: 'Locked'
          }
        };

      default:
        return {
          Icon: {
            icon: faCircle,
            iconColor: 'muted'
          },
          Badge: null
        };
    }
  };

  return (
    <Box data-selector={`${id}-div`} borderBottomWidth="2px">
      <Button
        onClick={getOnClick()}
        data-selector={selector}
        size="lg"
        variant="ghost"
        colorScheme={isClickable ? 'blue' : 'gray'}
        isDisabled={!isInteractive}
        borderRadius="none"
        textAlign="left"
        alignItems="start"
        gap={{ base: 1, lg: 2 }}
        px={{ base: 0, lg: 4 }}
        py={5}
        height="auto"
        width="100%"
        _disabled={{ opacity: 1, cursor: 'not-allowed' }}
      >
        <Box
          as="span"
          display="block"
          color={getProps(status).Icon.iconColor}
          width={6}
          lineHeight={6}
          flexShrink="0"
          mr={{ base: 1, lg: 2 }}
        >
          <FontAwesomeIcon icon={getProps(status).Icon.icon} size="lg" />
        </Box>

        <Box as="span" display="block" fontSize="lg" lineHeight="6" flexGrow="1" whiteSpace="normal">
          <Box fontWeight="medium">
            {status === Status.Rejected ? (
              <Box as="del" color="muted">
                {label}
              </Box>
            ) : (
              label
            )}
          </Box>

          {reason ? (
            <Collapse in={isOpen} animateOpacity>
              <Box as="blockquote" borderLeftWidth="4px" px={3} mt={3}>
                <Box fontWeight="medium" color="muted" mb={2}>
                  Reason for document rejection:
                </Box>

                {reason}
              </Box>
            </Collapse>
          ) : null}
        </Box>

        {getProps(status).Badge ? (
          <Box as="span" display="block">
            <Badge fontWeight="medium" {...getProps(status).Badge} />
          </Box>
        ) : null}

        {isClickable ? (
          <Box width={6} lineHeight={6}>
            <FontAwesomeIcon icon={faChevronRight} fixedWidth className="utility-icon" />
          </Box>
        ) : null}

        {reason ? (
          <Box width={6} lineHeight={6}>
            <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} fixedWidth className="utility-icon" />
          </Box>
        ) : null}

        {getOnClick() === undefined ? <Box width={6} /> : null}
      </Button>
    </Box>
  );
};

export default Finger;
